/**
 * Persistent offline banner shown when device loses network connectivity.
 *
 * Fixed at the top of the viewport with an accessible status announcement.
 * Slides in/out with animation (respects prefers-reduced-motion).
 * Uses useOnlineStatus hook internally — no props required.
 *
 * Tier 1.
 *
 * @module OfflineBanner
 */

import * as React from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { useOnlineStatus } from '@/hooks/useOnlineStatus';

/**
 * Global offline indicator banner.
 *
 * Renders a fixed-top amber banner when the device is offline.
 * Auto-dismisses when connectivity returns.
 *
 * @returns Banner element or null
 */
export function OfflineBanner(): React.ReactElement {
  const isOnline = useOnlineStatus();

  return (
    <AnimatePresence>
      {!isOnline && (
        <motion.div
          key="offline-banner"
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed top-0 right-0 left-0 z-50 flex items-center justify-center gap-2 bg-warning px-4 py-2.5 text-warning-foreground motion-reduce:transition-none"
          role="status"
          aria-live="polite"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 shrink-0"
            aria-hidden="true"
          >
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          </svg>
          <span className="text-sm font-medium">You&apos;re offline</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
